// src/screens/create_issue.screen.jsx
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosClient from "../axiosClient";
import { useStateContext } from "../context/ContextProvider";

import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";

const CreateIssueScreen = () => {
  const { t } = useTranslation();
  const { token } = useStateContext();

  // Refs
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const locationRef = useRef(null);

  // State
  const [priority, setPriority] = useState("MEDIUM");
  const [status, setStatus] = useState("OPEN");

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState();
  const [error, setError] = useState();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(undefined);
    setError(undefined);

    const title = titleRef.current?.value?.trim() || "";
    const description = descriptionRef.current?.value?.trim() || "";
    const location = locationRef.current?.value?.trim() || "";

    const localErrors = {};
    if (!title || title.length < 3) localErrors.title = "Title must be at least 3 characters";
    if (!description || description.length < 5)
      localErrors.description = "Description must be at least 5 characters";

    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      return;
    }
    setErrors({});

    const payload = {
      title,
      description,
      location,
      priority, // "LOW" | "MEDIUM" | "HIGH"
      status,   // "OPEN" | "IN_PROGRESS" | "CLOSED"
    };

    try {
      await axiosClient.post("/api/issues/", payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `JWT-${token}`,
        },
      });

      setMessage("Issue created successfully");
      titleRef.current.value = "";
      descriptionRef.current.value = "";
      locationRef.current.value = "";
      setPriority("MEDIUM");
      setStatus("OPEN");
    } catch (err) {
      const data = err?.response?.data;
      const allIssues = Object.keys(data || {});
      setError(
        data?.detail ||
          (allIssues.length ? data[allIssues[0]]?.[0] : null) ||
          "Failed to create issue. Please try again."
      );
      setErrors(data || {});
      // console.log(err);
    }
  };

  const handleReset = () => {
    setErrors({});
    setError(undefined);
    setMessage(undefined);
    setPriority("MEDIUM");
    setStatus("OPEN");
    if (titleRef.current) titleRef.current.value = "";
    if (descriptionRef.current) descriptionRef.current.value = "";
    if (locationRef.current) locationRef.current.value = "";
  };

  return (
    <Card elevation={16}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ fontSize: "1.2rem" }}>
          {t("Create Issue")}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ "& .MuiTextField-root": { mb: 2 } }}>
            <TextField
              fullWidth
              label={t("Title")}
              inputRef={titleRef}
              InputLabelProps={{
                required: true,
                sx: { "& .MuiFormLabel-asterisk": { color: "red" }, fontSize: "0.9rem" },
              }}
              InputProps={{ sx: { fontSize: "0.9rem" } }}
              onChange={() => setErrors((p) => ({ ...p, title: "" }))}
            />
            {errors?.title && (
              <Typography sx={{ textAlign: "left", mb: 1, fontSize: "0.8rem" }} color="error">
                {errors.title}
              </Typography>
            )}

            <TextField
              fullWidth
              label={t("Description")}
              inputRef={descriptionRef}
              multiline
              minRows={4}
              InputLabelProps={{
                required: true,
                sx: { "& .MuiFormLabel-asterisk": { color: "red" }, fontSize: "0.9rem" },
              }}
              InputProps={{ sx: { fontSize: "0.9rem" } }}
              onChange={() => setErrors((p) => ({ ...p, description: "" }))}
            />
            {errors?.description && (
              <Typography sx={{ textAlign: "left", mb: 1, fontSize: "0.8rem" }} color="error">
                {errors.description}
              </Typography>
            )}

            <TextField
              fullWidth
              label={t("Location (optional)")}
              inputRef={locationRef}
              InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
              InputProps={{ sx: { fontSize: "0.9rem" } }}
              onChange={() => setErrors((p) => ({ ...p, location: "" }))}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
            <FormControl>
              <FormLabel sx={{ fontSize: "0.9rem" }}>{t("Priority")}</FormLabel>
              <RadioGroup
                row
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
              >
                <FormControlLabel value="LOW" control={<Radio size="small" />} label={t("Low")} />
                <FormControlLabel
                  value="MEDIUM"
                  control={<Radio size="small" />}
                  label={t("Medium")}
                />
                <FormControlLabel value="HIGH" control={<Radio size="small" />} label={t("High")} />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel sx={{ fontSize: "0.9rem" }}>{t("Status")}</FormLabel>
              <RadioGroup
                row
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
              >
                <FormControlLabel value="OPEN" control={<Radio size="small" />} label={t("Open")} />
                <FormControlLabel
                  value="IN_PROGRESS"
                  control={<Radio size="small" />}
                  label={t("In Progress")}
                />
                <FormControlLabel
                  value="CLOSED"
                  control={<Radio size="small" />}
                  label={t("Closed")}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Button type="submit" variant="contained" sx={{ mt: 2, fontSize: "0.9rem" }}>
            {t("Submit")}
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleReset}
            sx={{ mt: 2, ml: 2, fontSize: "0.9rem" }}
          >
            {t("Reset")}
          </Button>

          {message && (
            <Box sx={{ my: 2 }}>
              <Alert severity="success" sx={{ fontSize: "0.9rem" }}>
                {message}
              </Alert>
            </Box>
          )}
          {error && (
            <Box sx={{ my: 2 }}>
              <Alert severity="error" sx={{ fontSize: "0.9rem" }}>
                {error}
              </Alert>
            </Box>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default CreateIssueScreen;